import {Button, Menu} from 'antd';
import Spin from 'antd/es/spin';
import cn from 'classnames';
import {isNil} from 'lodash-es';
import React, {
    useEffect, useRef, useState, FC, Key,
} from 'react';

import {ActionBar} from 'components/@common/widgets/action-bar';
import {CustomCard} from 'components/@common/widgets/custom-card';
import {KebabMenu} from 'components/@common/widgets/kebab-menu';
import {ParametersFilter} from 'components/@common/widgets/parameters-filter';
import {IParametersFilterRef} from 'components/@common/widgets/parameters-filter/parameters-filter';
import {AIS_ROUTES} from 'components/ais/ais.constants';
import {IconsMap} from 'components/dynamic-icon';
import {EFormMode} from 'components/form';
import {IModalOpenerComponentRef} from 'components/modal-opener-component';
import {Spinner} from 'components/spinner';
import {ClearTableColumnFiltersButton} from 'components/table-report/components/table-report-actions/actions/clear-table-column-filters-button';
import {UIBlocker} from 'components/ui-blocker';
import {CONTAINER_SELECTORS} from 'features/horizontal-scroll/model/constants';
import {HorizontalScroll} from 'features/horizontal-scroll/ui/horizontal-scroll';
import {selectIsFileLoading} from 'features/provide-custom-link';
import {selectContextRawData} from 'modules/context/context-selector';
import {createActionGetFile} from 'modules/data/data-actions';
import {ReactComponent as ReloadOutlined} from 'shared/assets/reload.svg';
import {ELocationStateKey, useAppHistory} from 'shared/hooks/use-app-history';
import {useUserRoleFunctions} from 'shared/hooks/use-user-role-functions';
import {ERequestType} from 'shared/types/requests';
import {isEmptyObject, omitEmptyLines} from 'shared/utils';
import {useAppDispatch, useAppSelector} from 'store/config/hooks';
import {aisSelectors, aisSliceActions} from 'store/slices/ais-slice/ais-slice';
import {TFetchAisDocumentsRegisterParams, transferAisDocumentsRegister} from 'store/slices/ais-slice/ais-slice-api';
import {
    loadAisDocumentsRegister,
    loadAisDocumentsRegisterFields,
    loadAisDocumentsRegisterFilterConditionsEntries,
    loadAisDocumentsRegisterSettings,
} from 'store/slices/ais-slice/ais-slice-thunks';
import {selectIsThunkPending, selectThunkError} from 'store/slices/loading-state-slice';
import {openModal} from 'store/slices/modals-slice';
import {EModalType} from 'store/slices/modals-slice/modals-slice-types';
import {EUserRoleFunction} from 'store/slices/user-slice/user-slice-role-functions';

import {AisDocumentsParametersFilter} from './ais-documents-parameters-filter';
import {AIS_REGISTER_ROLE_SECTION, URL_DOWNLOAD_EXCEL} from './ais-documents-register-constants';
import {AisDocumentsRegisterTable} from './ais-documents-register-table';
import {
    TDocumentsRegisterParametersFilter,
    IDocumentsRegisterParametersFilterForm,
    TDocumentsRegisterRequestQueryRef,
} from './ais-documents-register-types';
import {extractFilterValuesFromRequestData} from './ais-documents-register-utils';
import {DeleteConfirmationModal} from './modals/delete-confirmation-modal';
import {DocumentCardModal} from './modals/document-card-modal';
import {SetActiveFlagModalConfirmation} from './modals/set-active-flag-modal-confirmation';

import './ais-documents-register.less';

interface IAisDocumentsRegisterProps {}

export const AisDocumentsRegister: FC<IAisDocumentsRegisterProps> = () => {
    const dispatch = useAppDispatch();
    const history = useAppHistory();
    const isFileLoading = useAppSelector(selectIsFileLoading);
    const {organizationId, taxPeriodId} = useAppSelector(selectContextRawData) || {};

    const {selectAll: selectAllDocuments} = aisSelectors.documentsSelectors;

    const documents = useAppSelector(selectAllDocuments);

    const isLoadingRegisterTableData = useAppSelector(s => selectIsThunkPending(
        s, loadAisDocumentsRegister.typePrefix,
    ));

    const registerTableLoadError = useAppSelector(s => selectThunkError(
        s, loadAisDocumentsRegister.typePrefix,
    ));

    const aisLocationState = history.currentState?.[ELocationStateKey.AIS_REGISTER];
    const linkFilterLocationState = history.currentState?.[ELocationStateKey.LINK_FILTER_PARAMETERS];

    const [documentsParametersFilter, setDocumentsParametersFilter] = useState<TDocumentsRegisterParametersFilter>();

    const {dateTo, dateFrom} = documentsParametersFilter ?? {};
    const isUploadCSVDisabled = !dateTo || !dateFrom;

    const tableRef = useRef(null);
    const documentsRequestQueryRef = useRef<TDocumentsRegisterRequestQueryRef>(null);
    const createDocumentModalRef = useRef<IModalOpenerComponentRef>(null);
    const deleteConfirmationModalRef = useRef<IModalOpenerComponentRef>(null);
    const setActiveFlagModalConfirmationRef = useRef<IModalOpenerComponentRef>(null); // достаём метод изменения
    // сортировок и фильтров из компонента таблицы

    const {hasUserRoleFunction} = useUserRoleFunctions({
        sections: [AIS_REGISTER_ROLE_SECTION],
    });

    const resetPage = () => {
        if (documentsParametersFilter) setDocumentsParametersFilter(p => ({...p})); // сброс страницы на 1-ю
    };

    const handleRefresh = () => {
        resetPage();
    };

    const parametersFilterRef = useRef<IParametersFilterRef<IDocumentsRegisterParametersFilterForm>>(null);

    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);

    const pageSettings = useAppSelector(aisSelectors.selectAisRegisterDocumentsSettings);
    const isSettingsLoading = useAppSelector(s => selectIsThunkPending(
        s, loadAisDocumentsRegisterSettings.typePrefix,
    ));

    const fields = useAppSelector(aisSelectors.selectAisRegisterDocumentsFields);
    const isFieldsLoading = useAppSelector(s => selectIsThunkPending(
        s, loadAisDocumentsRegisterFields.typePrefix,
    ));

    const aisDocumentsRegisterFilterConditionsEntries = useAppSelector(
        aisSelectors.selectAisRegisterDocumentsFilterConditionsEntries,
    );

    const isAisDocumentsRegisterFilterConditionsEntriesLoading = useAppSelector(s => selectIsThunkPending(
        s, loadAisDocumentsRegisterFilterConditionsEntries.typePrefix,
    ));

    const tableColumnsFiltersAdaptedForRequest = useAppSelector(
        aisSelectors.selectAisRegisterColumnsFiltersDataAdaptedForRequest,
    );

    useEffect(() => {
        if (!pageSettings) dispatch(loadAisDocumentsRegisterSettings());
        if (!fields) dispatch(loadAisDocumentsRegisterFields());
        if (!aisDocumentsRegisterFilterConditionsEntries) dispatch(loadAisDocumentsRegisterFilterConditionsEntries());
    }, [organizationId, taxPeriodId]);

    const isLoading = isSettingsLoading
    || isFieldsLoading
    || isAisDocumentsRegisterFilterConditionsEntriesLoading;

    useEffect(() => {
        if (
            !fields
          || !pageSettings
          || isAisDocumentsRegisterFilterConditionsEntriesLoading
        ) return;

        if (aisLocationState && aisLocationState.requests.requestId !== undefined) {
            parametersFilterRef.current?.setInitialFormValues({
                ...extractFilterValuesFromRequestData({
                    ...linkFilterLocationState,
                    ...aisLocationState?.requests?.requestData,
                }),
            });
            return;
        }

        if (!isNil(linkFilterLocationState) && !isEmptyObject(linkFilterLocationState)) {
            parametersFilterRef.current?.setInitialFormValues({...linkFilterLocationState});
        }
    }, [isLoading, fields, pageSettings, linkFilterLocationState, aisLocationState?.requests?.requestId]);

    const handleTransfer = () => {
        transferAisDocumentsRegister();
        handleRefresh();
    };

    const handleUploadCSV = () => {
        dispatch(createActionGetFile({
            referenceUrl: 'integration.module/register.documents/download/csv',
            requestType: ERequestType.POST,
            body: {
                dateFrom,
                dateTo,
            },
            isMessage: true,
        }));
    };

    const handleDownloadExcel = () => {
        const params = documentsRequestQueryRef.current?.getParams();
        const fetchedData : TFetchAisDocumentsRegisterParams = {
            dateFrom,
            dateTo,
            filters: tableColumnsFiltersAdaptedForRequest,
            ...params,
        };
        dispatch(
            createActionGetFile({
                referenceUrl: URL_DOWNLOAD_EXCEL,
                requestType: ERequestType.POST,
                body: fetchedData,
            }),
        );
    };
    return (
        <div className={cn('ais-documents-register')}>
            <UIBlocker
                content={isFileLoading ? (
                    <Spinner
                        tip="Загрузка файла..."
                    />
                ) : undefined}
            >
                <CustomCard
                    isLoading={isLoading}
                    accordionTransitionType="1"
                    title="Реестр документов, размещённых в информационной системе организации"
                    accordionContent={pageSettings?.useParameters && (
                        <ParametersFilter<IDocumentsRegisterParametersFilterForm>
                            ref={parametersFilterRef}
                            onChange={values => {
                                setDocumentsParametersFilter(omitEmptyLines(values));
                            }}
                            onClear={() => {
                                dispatch(aisSliceActions.resetRegisterDocuments());
                            }}
                            className={cn('ais-documents-register__filters')}
                        >
                            {AisDocumentsParametersFilter}
                        </ParametersFilter>
                    )}
                    isAccordionContentVisibleByDefault={!aisLocationState?.requests?.requestData}
                >
                    <ActionBar>
                        {/* todo: вернуть свич по разделам как будет реализован Реестр документов по налогам */}
                        {/* <CustomRadio */}
                        {/*    defaultValue="reg" */}
                        {/*    items={[ */}
                        {/*        {label: 'Реестр документов', value: 'reg'}, */}
                        {/*        { */}
                        {/*            label: 'Реестр документов по налогам', */}
                        {/*            value: 'tax_reg', */}
                        {/*            onClick: () => { */}
                        {/*                history.push(`${AIS_ROUTES.DOCUMENTS_BY_TAX_REGISTER}`); */}
                        {/*            }, */}
                        {/*        }]} */}
                        {/* /> */}
                        <KebabMenu placeholder={[
                            EUserRoleFunction.CREATE,
                            EUserRoleFunction.DELETE,
                            EUserRoleFunction.AIS_PUBLISH,
                            EUserRoleFunction.DOWNLOAD_EXCEL,
                            EUserRoleFunction.XX_EXT_UPLOAD_DOC_JOURNAL,
                            EUserRoleFunction.DOWNLOAD_TEMPLATE,
                            EUserRoleFunction.UPLOAD_EXCEL,
                            EUserRoleFunction.XX_NALMON_UPLOAD_DOC_JOURNAL,
                        ].every(f => !hasUserRoleFunction(f)) ? <>Нет доступных действий</> : undefined}
                        >
                            <Menu>
                                {hasUserRoleFunction(EUserRoleFunction.CREATE) && (
                                    <Menu.Item onClick={() => {
                                        createDocumentModalRef.current?.showModal();
                                    }}
                                    >Добавить запись
                                    </Menu.Item>
                                )}

                                {hasUserRoleFunction(EUserRoleFunction.DELETE) && (
                                    <Menu.Item
                                        disabled={!selectedRowKeys.length}
                                        onClick={() => {
                                            deleteConfirmationModalRef.current?.showModal();
                                        }}
                                    >Удалить запись
                                    </Menu.Item>
                                )}

                                {hasUserRoleFunction(EUserRoleFunction.AIS_PUBLISH) && (
                                    <Menu.Item
                                        disabled={!selectedRowKeys.length}
                                        onClick={() => {
                                            setActiveFlagModalConfirmationRef.current?.showModal();
                                        }}
                                    >Сделать доступным для отправки в АИС
                                    </Menu.Item>
                                )}

                                {hasUserRoleFunction(EUserRoleFunction.DOWNLOAD_EXCEL) && (
                                    <Menu.Item
                                        onClick={() => {
                                            dispatch(openModal({
                                                entityName: AIS_ROUTES.DOCUMENTS_REGISTER,
                                                modalName: EModalType.documentsFileUpload,
                                            }));
                                        }}
                                    >
                                        Загрузить Excel
                                    </Menu.Item>
                                )}

                                {hasUserRoleFunction(EUserRoleFunction.XX_EXT_UPLOAD_DOC_JOURNAL)
                                    && <Menu.Item>Выгрузить из внешней системы</Menu.Item>}

                                {hasUserRoleFunction(EUserRoleFunction.DOWNLOAD_TEMPLATE)
                                    && <Menu.Item>Скачать шаблон</Menu.Item>}

                                {hasUserRoleFunction(EUserRoleFunction.UPLOAD_EXCEL)
                                    && (
                                        <Menu.Item
                                            onClick={handleDownloadExcel}
                                        >
                                            Выгрузить отчёт в Excel
                                        </Menu.Item>
                                    )}

                                {hasUserRoleFunction(EUserRoleFunction.XX_NALMON_UPLOAD_DOC_JOURNAL) && (
                                    <Menu.Item
                                        title="Поиск и перенос данных из Витрины"
                                        onClick={() => {
                                            handleTransfer();
                                        }}
                                    >
                                        Выгрузить документы из витрины
                                    </Menu.Item>
                                )}

                                {hasUserRoleFunction(EUserRoleFunction.UPLOAD_CSV) && (
                                    <Menu.Item
                                        title="Выгрузка данных в csv файл"
                                        onClick={() => {
                                            handleUploadCSV();
                                        }}
                                        disabled={isUploadCSVDisabled}
                                    >
                                        Выгрузить в csv
                                    </Menu.Item>
                                )}

                                <DocumentCardModal
                                    ref={createDocumentModalRef}
                                    resetPage={resetPage}
                                    initialFormMode={EFormMode.CREATE}
                                />
                                <DeleteConfirmationModal
                                    ref={deleteConfirmationModalRef}
                                    selectedRowKeys={selectedRowKeys}
                                    handleRefresh={handleRefresh}
                                    setSelectedRowKeys={setSelectedRowKeys}
                                />
                                <SetActiveFlagModalConfirmation
                                    ref={setActiveFlagModalConfirmationRef}
                                    documentIds={selectedRowKeys}
                                    setDocumentIds={setSelectedRowKeys}
                                />
                            </Menu>
                        </KebabMenu>

                        <Button
                            disabled={!documentsParametersFilter || isLoadingRegisterTableData}
                            type="default"
                            onClick={() => {
                                handleRefresh();
                            }}
                            icon={<ReloadOutlined />}
                        >
                            Обновить
                        </Button>

                        <>
                            {aisLocationState && (
                                <Button
                                    type="default"
                                    onClick={() => {
                                        const {requestId} = aisLocationState?.requests ?? {};
                                        if (requestId) {
                                            history.push({
                                                pathname: `/requests/${requestId}`,
                                            });
                                        }
                                    }}
                                >
                                    <IconsMap.ArrowBackOutlined />
                                    Вернуться к запросу
                                </Button>
                            )}
                        </>

                        <ClearTableColumnFiltersButton
                            setTableColumnFilters={aisSliceActions.setRegisterDocumentsColumnsFilters}
                            selectTableFiltersDataAdaptedForRequest={
                                aisSelectors.selectAisRegisterColumnsFiltersDataAdaptedForRequest
                            }
                        />
                    </ActionBar>
                    <Spin
                        spinning={isLoadingRegisterTableData}
                        tip="Загрузка данных реестра..."
                    >
                        <UIBlocker
                            thunkError={{
                                error: registerTableLoadError,
                                verbose: true,
                            }}
                            extra={(
                                <>
                                    <Button
                                        type="default"
                                        onClick={() => {
                                            handleRefresh();
                                        }}
                                        icon={<ReloadOutlined />}
                                    >
                                        Обновить
                                    </Button>
                                </>
                            )}
                        >
                            <AisDocumentsRegisterTable
                                documentsParametersFilter={documentsParametersFilter}
                                selectedRowKeys={selectedRowKeys}
                                setSelectedRowKeys={setSelectedRowKeys}
                                documentsRequestQueryRef={documentsRequestQueryRef}
                                tableRef={tableRef}
                            />
                            <HorizontalScroll
                                containerRef={tableRef}
                                scrollableSelector={CONTAINER_SELECTORS.TABLE}
                                enabled={documents?.length > 0}
                            />
                        </UIBlocker>
                    </Spin>
                </CustomCard>
            </UIBlocker>
        </div>
    );
};
